import { PetProfile } from '../types/onboarding';

/**
 * Pet Confirmation Service
 * 
 * Handles pet identity confirmation when pet names are detected in user questions.
 * This ensures the assistant is working with the correct pet before proceeding.
 */

export interface PetConfirmationRequest {
  detectedPetName: string;
  matchingPets: PetProfile[];
  userQuestion: string;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
}

export interface PetConfirmationResult {
  needsConfirmation: boolean;
  confirmationMessage: string;
  selectedPet?: PetProfile;
  shouldProceed: boolean;
}

export class PetConfirmationService {
  
  /**
   * Check if pet confirmation is needed and generate confirmation message
   */
  checkPetConfirmation(request: PetConfirmationRequest): PetConfirmationResult {
    const { detectedPetName, matchingPets, userLanguage } = request;
    
    // If no pet name detected, no confirmation needed
    if (!detectedPetName) {
      return {
        needsConfirmation: false,
        confirmationMessage: '',
        shouldProceed: true
      };
    }
    
    // If no matching pets found, no confirmation needed
    if (matchingPets.length === 0) {
      return {
        needsConfirmation: false,
        confirmationMessage: '',
        shouldProceed: true
      };
    }
    
    // If exactly one matching pet found, ask for confirmation
    if (matchingPets.length === 1) {
      const pet = matchingPets[0];
      const confirmationMessage = this.generateConfirmationMessage(detectedPetName, pet, userLanguage);
      
      return {
        needsConfirmation: true,
        confirmationMessage,
        selectedPet: pet,
        shouldProceed: false
      };
    }
    
    // If multiple matching pets found, ask user to choose
    if (matchingPets.length > 1) {
      const confirmationMessage = this.generateMultiplePetsMessage(detectedPetName, matchingPets, userLanguage);
      
      return {
        needsConfirmation: true,
        confirmationMessage,
        shouldProceed: false
      };
    }
    
    return {
      needsConfirmation: false,
      confirmationMessage: '',
      shouldProceed: true
    };
  }
  
  /**
   * Generate confirmation message for single pet match
   */
  private generateConfirmationMessage(
    detectedName: string, 
    pet: PetProfile, 
    language: string
  ): string {
    const petTypeText = this.getPetTypeText(pet.type, language);
    
    switch (language) {
      case 'zh-CN':
        return `我注意到您提到了"${detectedName}"。请问${detectedName}是您的${petTypeText}吗？`;
      
      case 'es':
        return `Noto que mencionaste "${detectedName}". ¿Es ${detectedName} tu ${petTypeText}?`;
      
      case 'fr':
        return `Je remarque que vous avez mentionné "${detectedName}". Est-ce que ${detectedName} est votre ${petTypeText}?`;
      
      default: // English
        return `I notice you mentioned "${detectedName}". Is ${detectedName} your ${petTypeText}?`;
    }
  }
  
  /**
   * Generate message for multiple pets with same name
   */
  private generateMultiplePetsMessage(
    detectedName: string, 
    pets: PetProfile[], 
    language: string
  ): string {
    const petList = pets.map(pet => {
      const petTypeText = this.getPetTypeText(pet.type, language);
      return `• ${pet.name} (${petTypeText})`;
    }).join('\n');
    
    switch (language) {
      case 'zh-CN':
        return `我注意到您提到了"${detectedName}"。我在您的宠物档案中找到了多个同名的宠物：\n\n${petList}\n\n请告诉我您指的是哪一个？`;
      
      case 'es':
        return `Noto que mencionaste "${detectedName}". Encontré múltiples mascotas con ese nombre en tu perfil:\n\n${petList}\n\n¿Cuál te refieres?`;
      
      case 'fr':
        return `Je remarque que vous avez mentionné "${detectedName}". J'ai trouvé plusieurs animaux avec ce nom dans votre profil:\n\n${petList}\n\nLequel voulez-vous dire?`;
      
      default: // English
        return `I notice you mentioned "${detectedName}". I found multiple pets with that name in your profile:\n\n${petList}\n\nWhich one are you referring to?`;
    }
  }
  
  /**
   * Get localized pet type text
   */
  private getPetTypeText(petType: 'dog' | 'cat' | 'other', language: string): string {
    switch (language) {
      case 'zh-CN':
        if (petType === 'dog') return '狗';
        if (petType === 'cat') return '猫';
        return '其他宠物';
      case 'es':
        if (petType === 'dog') return 'perro';
        if (petType === 'cat') return 'gato';
        return 'otra mascota';
      case 'fr':
        if (petType === 'dog') return 'chien';
        if (petType === 'cat') return 'chat';
        return 'autre animal';
      default: // English
        return petType;
    }
  }
  
  /**
   * Process user's confirmation response
   */
  processConfirmationResponse(
    userResponse: string, 
    expectedPet: PetProfile, 
    language: 'en' | 'zh-CN' | 'es' | 'fr'
  ): { confirmed: boolean; selectedPet?: PetProfile } {
    const response = userResponse.toLowerCase().trim();
    
    // Check for positive responses in different languages
    const positiveResponses: Record<'en' | 'zh-CN' | 'es' | 'fr', string[]> = {
      'en': ['yes', 'y', 'correct', 'right', 'that\'s right', 'yes it is', 'yes, it is'],
      'zh-CN': ['是', '是的', '对', '对的', '正确', '没错', '是的，是', '是的，它是'],
      'es': ['sí', 'si', 'correcto', 'exacto', 'así es', 'sí es', 'sí, es'],
      'fr': ['oui', 'exact', 'correct', 'c\'est ça', 'oui c\'est', 'oui, c\'est']
    };
    
    const positiveWords = positiveResponses[language] || positiveResponses['en'];
    const isPositive = positiveWords.some((word: string) => response.includes(word));
    
    if (isPositive) {
      return {
        confirmed: true,
        selectedPet: expectedPet
      };
    }
    
    return {
      confirmed: false
    };
  }
}

export const petConfirmationService = new PetConfirmationService();
